import { useFormik } from "formik";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import * as Yup from "yup";

import { CreateAndDetailLayout } from "../../../components/CreateAndDetailLayout";
import { ButtonCheck } from "../../../components/Form/ButtonCheck";
import { GroupInput } from "../../../components/Form/GroupInput";
import { Input } from "../../../components/Form/Input";
import { InputCheckboxV2 } from "../../../components/Form/InputCheckboxV2";
import { PanelAndDetailAndCreateHeader } from "../../../components/panel/PanelAndDetailAndCreateHeader";
import { Button } from "../../../components/ui/button";
import { useBrands } from "../../../hook/queries/useBrands";
import api from "../../../service/api";
import { ContainerForm, Form } from "../styles";

const CreateUser: React.FC = () => {
  const { addToast } = useToasts();
  const history = useHistory();
  const getBrands = useBrands(1, { active: 1 }, 9999);

  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(data: any) {
    setIsLoading(true);
    try {
      await api.post("/provider", data);

      addToast("Fornecedor criado com sucesso", {
        appearance: "success",
        autoDismiss: true,
      });
      history.push("/admin/fornecedores");
    } catch (err) {
      const error = err as any;

      if (!error.response) {
        return addToast(
          "Desculpe, ocorreu um erro interno, Tente novamente mais tarde",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (error.response.data.error === "email already exists") {
        return addToast("Este e-mail já existe em nossa base de dados", {
          appearance: "warning",
          autoDismiss: true,
        });
      }

      if (error.response.data.error === "cod already exists") {
        return addToast("Este Código ERP já existe em nossa base de dados", {
          appearance: "warning",
          autoDismiss: true,
        });
      }

      if (error.response.status === 401) {
        return addToast("Sem permissão para esta ação", {
          appearance: "warning",
          autoDismiss: true,
        });
      }

      return addToast(
        "Desculpe, ocorreu um erro interno, Tente novamente mais tarde",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
    } finally {
      setIsLoading(false);
    }
  }

  const formik = useFormik({
    initialValues: {
      cod: undefined,
      fullname: undefined,
      phone: undefined,
      email: undefined,
      password: undefined,
      isShippingCompany: false,
      isBrand: false,
      brands: [],
    },
    validationSchema: Yup.object({
      fullname: Yup.string().required("Nome completo é obrigatório"),
      phone: Yup.string().required("Telefone é obrigatório"),

      email: Yup.string()
        .email("informe um e-mail válido")
        .required("email é obrigatório"),
      password: Yup.string()
        .min(8, "A senha precisa conter no mínimo 8 caracteres.")
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
          "A senha precisa conter letrar e numeros, no mínimo 8 caracteres e um caracter especial."
        )
        .required("Senha é obrigatório"),
    }),
    onSubmit: (data) => handleSubmit(data),
  });

  return (
    <CreateAndDetailLayout isLoading={getBrands?.isLoading}>
      <PanelAndDetailAndCreateHeader title={"Criar fornecedor"} goBack />

      <ContainerForm>
        <Form onSubmit={formik.handleSubmit}>
          <Input
            label="Código ERP"
            {...formik.getFieldProps("cod")}
            error={
              formik.touched.cod && formik.errors.cod
                ? formik.errors.cod
                : undefined
            }
          />

          <Input
            label="Nome"
            {...formik.getFieldProps("fullname")}
            error={
              formik.touched.fullname && formik.errors.fullname
                ? formik.errors.fullname
                : undefined
            }
          />

          <ButtonCheck
            label="É transportadora"
            bgColor="#11111d"
            name="status"
            value={formik.values.isShippingCompany}
            setValue={(data) => {
              formik.setFieldValue("isShippingCompany", data);
            }}
            options={[
              {
                value: true,
                field: "SIM",
              },
              {
                value: false,
                field: "NÃO",
              },
            ]}
            error={
              formik.touched.isShippingCompany &&
              formik.errors.isShippingCompany
                ? formik.errors.isShippingCompany
                : undefined
            }
          />

          <ButtonCheck
            label="É marca"
            bgColor="#11111d"
            name="status"
            value={formik.values.isBrand}
            setValue={(data) => {
              formik.setFieldValue("isBrand", data);
            }}
            options={[
              {
                value: true,
                field: "SIM",
              },
              {
                value: false,
                field: "NÃO",
              },
            ]}
            error={
              formik.touched.isBrand && formik.errors.isBrand
                ? formik.errors.isBrand
                : undefined
            }
          />

          <GroupInput>
            <Input
              label="Email"
              {...formik.getFieldProps("email")}
              error={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : undefined
              }
            />
            <Input
              label="Telefone"
              {...formik.getFieldProps("phone")}
              error={
                formik.touched.phone && formik.errors.phone
                  ? formik.errors.phone
                  : undefined
              }
            />
          </GroupInput>

          <InputCheckboxV2
            label="Marcas"
            checks={formik.values.brands}
            data={
              getBrands?.data?.map((brand) => ({
                description: brand.name,
                value: brand.id.toString(),
              })) ?? []
            }
            name="brands"
            onChange={(e) => {
              formik.setFieldValue("brands", e);
            }}
            error={
              formik.touched.brands && formik.errors.brands
                ? formik.errors.brands.toString()
                : undefined
            }
          />

          <Input
            label="Senha"
            {...formik.getFieldProps("password")}
            error={
              formik.touched.password && formik.errors.password
                ? formik.errors.password
                : undefined
            }
          />

          <Button type="submit" isLoading={isLoading}>
            Criar
          </Button>
        </Form>
      </ContainerForm>
    </CreateAndDetailLayout>
  );
};

export default CreateUser;
